import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const sections = [
  {
    title: "Agreement to Terms",
    content:
      "By accessing or using the Mosheli website or engaging our services, you agree to these Terms & Conditions and our Privacy Policy. If you do not agree, please do not use our site or services.",
  },
  {
    title: "Services and Proposals",
    content:
      "Project scopes, timelines, and pricing are defined in written proposals or statements of work. We begin work after a signed agreement and any required deposit are received. Changes to scope may affect timelines and costs and will be documented in writing.",
  },
  {
    title: "Client Responsibilities",
    content:
      "Clients are responsible for providing accurate information, timely feedback, and required assets. Delays in approvals or content delivery may affect delivery timelines. You confirm that materials you provide do not infringe on third-party rights.",
  },
  {
    title: "Payments",
    content:
      "Invoices are due according to the payment schedule outlined in your agreement. Late payments may result in paused work or delayed delivery. All payments are non-refundable for work already completed or expenses incurred.",
  },
  {
    title: "Intellectual Property",
    content:
      "Upon full payment, you own the final deliverables described in your agreement. Mosheli retains ownership of pre-existing tools, frameworks, and reusable components. We reserve the right to showcase completed work in our portfolio unless otherwise agreed in writing.",
  },
  {
    title: "Third-Party Services",
    content:
      "We may integrate third-party tools such as hosting providers, analytics platforms, or marketing services. Use of those services is subject to their respective terms, and we are not responsible for their performance or policies.",
  },
  {
    title: "Confidentiality",
    content:
      "We treat confidential information shared during a project with care and use it only for the purpose of delivering agreed services. We can sign mutual NDAs upon request.",
  },
  {
    title: "Warranty and Support",
    content:
      "We provide a limited warranty period after launch to address bugs attributable to our work. Ongoing maintenance, hosting, or support services require a separate agreement.",
  },
  {
    title: "Limitation of Liability",
    content:
      "Mosheli is not liable for indirect, incidental, or consequential damages arising from the use of our services or website. Our total liability is limited to the amounts paid for the specific services giving rise to the claim.",
  },
  {
    title: "Termination",
    content:
      "Either party may terminate a project with written notice. The client is responsible for payment of work completed up to the termination date. Upon termination, Mosheli will deliver any completed work that has been paid for.",
  },
  {
    title: "Governing Law",
    content:
      "These terms are governed by the laws of the State of Illinois, without regard to conflict-of-law principles.",
  },
  {
    title: "Contact",
    content:
      "If you have questions about these Terms & Conditions, contact us at create@mosheli.com.",
  },
];

export default function TermsConditions() {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-background" />
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Terms & <span className="text-gradient">Conditions</span>
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Effective date: January 25, 2026
            </motion.p>
            <motion.p
              className="text-base text-muted-foreground"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              These terms define how we work together and the expectations for using our website.
            </motion.p>
          </div>
        </section>

        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto space-y-10">
            {sections.map((section, index) => (
              <motion.div
                key={section.title}
                className="p-6 md:p-8 rounded-2xl bg-card border border-border"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <h2 className="text-2xl font-semibold mb-3">{section.title}</h2>
                <p className="text-muted-foreground leading-relaxed">{section.content}</p>
              </motion.div>
            ))}
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}